import { useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import "../../../auth/auth.css";
import { Modal } from "../../../auth/Modal.tsx";
import { useServiceAdmin } from "./use-service-admin.ts";
import {
  LIFECYCLE_QUERY_ROOT,
  PROBATION_SETTINGS_PATH,
  describeObservation,
  incidentsPath,
  lifecycleMessage,
  lifecycleWrite,
  newOpId,
  reinstatePath,
  suspendPath,
  type HostDecision,
  type HostObservation,
  type Incident,
  type IncidentsResponse,
  type ProbationSettings,
  type TimelineEntry,
} from "./agent-lifecycle-client.ts";
import type { StatusLevel } from "../../../components/index.ts";
import { OtpInput } from "../../../components/OtpInput.tsx";
import { formatDateTime, isoAttr } from "../../../lib/datetime.ts";

/**
 * The agent kill switch in the AIS console (G2 #138): a Suspend / Reinstate button for team
 * administrators, the incident timeline for everyone who can read the agent, and the team's probation
 * settings. The buttons only reflect what the server will allow; the BFF and the AIS enforce the admin
 * role, the reason, the step-up and the tenant regardless of what is shown here.
 */

export interface LifecycleAgent {
  readonly agentId: string;
  readonly agentRef?: string;
  readonly label?: string;
  readonly status?: string;
  readonly revoked?: boolean;
  readonly probationUntil?: string | null;
  readonly lastHostObservation?: HostObservation | null;
}

/** The display state of an agent for the list badge and the detail header. */
export function lifecycleBadge(agent: LifecycleAgent, nowMs: number): { level: StatusLevel; label: string } {
  if (agent.revoked === true || agent.status === "revoked") return { level: "error", label: "Revoked" };
  if (agent.status === "suspended") return { level: "warn", label: "Suspended" };
  const until = agent.probationUntil === null || agent.probationUntil === undefined ? Number.NaN : Date.parse(agent.probationUntil);
  if (Number.isFinite(until) && until > nowMs) return { level: "warn", label: "Probation" };
  return { level: "healthy", label: "Active" };
}

const TIMELINE_LABEL: Readonly<Record<string, string>> = {
  suspended: "Suspended",
  reinstated: "Reinstated",
  probation_ended: "Probation ended",
  closed: "Incident closed",
};

const DECISION_LABEL: Readonly<Record<HostDecision, string>> = {
  matched: "The agent is back on its enrolled host",
  accept_new_host: "Accept the new host the agent called in from",
  accept_unbound: "Reinstate an agent that has no enrolled host",
};

function when(iso: string): string {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? formatDateTime(ms) : iso;
}

export function AgentLifecyclePanel(props: { readonly agent: LifecycleAgent; readonly isAdmin: boolean }): ReactNode {
  const { agent, isAdmin } = props;
  const ref = agent.agentRef;
  const [modal, setModal] = useState<"suspend" | "reinstate" | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const queryClient = useQueryClient();
  const incidents = useServiceAdmin<IncidentsResponse>("ais", ref === undefined ? "agents" : incidentsPath(ref), ["agents", ref ?? "none", "incidents"], {
    enabled: ref !== undefined,
  });
  const status = agent.status ?? "active";
  const suspended = status === "suspended";
  const canSuspend = isAdmin && ref !== undefined && status === "active" && agent.revoked !== true;
  const canReinstate = isAdmin && ref !== undefined && suspended;
  const data = incidents.data;
  const nowMs = Date.now();

  const refresh = async (message: string): Promise<void> => {
    setModal(null);
    setNotice(message);
    await queryClient.invalidateQueries({ queryKey: LIFECYCLE_QUERY_ROOT });
  };

  return (
    <section className="svc-lifecycle" aria-label="Kill switch">
      <div className="svc-adm2__card-head">
        <h4 className="svc-admin__section-title">Kill switch</h4>
        {isAdmin && (
          <div className="svc-admin__actions">
            {canSuspend && (
              <button type="button" className="svc-admin__btn svc-admin__btn--danger" onClick={(e) => { triggerRef.current = e.currentTarget; setNotice(null); setModal("suspend"); }}>
                Suspend
              </button>
            )}
            {canReinstate && (
              <button type="button" className="svc-admin__btn" onClick={(e) => { triggerRef.current = e.currentTarget; setNotice(null); setModal("reinstate"); }}>
                Reinstate
              </button>
            )}
          </div>
        )}
      </div>

      {notice !== null && <p className="svc-admin__muted" role="status">{notice}</p>}
      {suspended && (
        <p className="svc-admin__muted">
          This agent is suspended. It gets no tokens, and calls made with a token issued before the suspension are
          denied after suspension (stale epoch), so they appear as identity revoked in the decision log.
        </p>
      )}
      {agent.probationUntil !== null && agent.probationUntil !== undefined && Date.parse(agent.probationUntil) > nowMs && (
        <p className="svc-admin__muted">
          On probation until <time dateTime={isoAttr(Date.parse(agent.probationUntil))}>{when(agent.probationUntil)}</time>.
          Read operations work; write operations are denied until then.
        </p>
      )}

      <IncidentTimeline query={incidents} hasRef={ref !== undefined} />
      {isAdmin && <ProbationSettingsForm current={data?.settings} onSaved={() => refresh("Probation settings saved.")} />}

      {/* Mounted only while open, so each opening starts with an empty form and a fresh op_id. */}
      {ref !== undefined && modal === "suspend" && (
        <SuspendModal
          open
          agentRef={ref}
          agentName={agent.label ?? agent.agentId}
          onClose={() => setModal(null)}
          onDone={() => refresh("Agent suspended. Its next token request and boundary decision are denied.")}
        />
      )}
      {ref !== undefined && modal === "reinstate" && (
        <>
          <ReinstateModal
            open
            agentRef={ref}
            agentName={agent.label ?? agent.agentId}
            incidentId={data?.incident_id ?? null}
            observation={data?.last_host_observation ?? agent.lastHostObservation ?? null}
            maxAgeS={data?.settings.host_observation_max_age_s ?? 900}
            probationSeconds={data?.settings.probation_seconds ?? 0}
            onClose={() => setModal(null)}
            onDone={() => refresh("Agent reinstated.")}
          />
        </>
      )}
    </section>
  );
}

function IncidentTimeline(props: {
  readonly query: UseQueryResult<IncidentsResponse>;
  readonly hasRef: boolean;
}): ReactNode {
  const { query } = props;
  const headingId = useId();
  if (!props.hasRef) return null;
  if (query.isError) {
    return <p className="svc-admin__err" role="alert">The incident timeline could not be loaded.</p>;
  }
  if (query.isPending || query.data === undefined) {
    return <p className="svc-admin__muted" role="status">Loading the incident timeline…</p>;
  }
  const incidents = query.data.incidents;
  return (
    <div className="svc-lifecycle__timeline" role="region" aria-labelledby={headingId}>
      <h4 className="svc-admin__section-title" id={headingId}>Incident timeline</h4>
      {incidents.length === 0 ? (
        <p className="svc-admin__muted">No incidents. This agent has not been suspended.</p>
      ) : (
        incidents.map((incident) => <IncidentCard key={incident.incident_id} incident={incident} />)
      )}
    </div>
  );
}

function incidentLabel(incident: Incident): string {
  if (incident.state === "closed") return `closed (${incident.closed_outcome ?? "completed"})`;
  return incident.state;
}

function IncidentCard(props: { readonly incident: Incident }): ReactNode {
  const { incident } = props;
  return (
    <article className="svc-lifecycle__incident" aria-label={`Incident ${incident.incident_id}`}>
      <p className="svc-lifecycle__incident-head">
        <code>{incident.incident_id}</code> <span className="svc-adm2__card-sub">{incidentLabel(incident)}</span>
      </p>
      <ol className="svc-lifecycle__entries">
        {incident.timeline.map((entry: TimelineEntry) => (
          <li key={entry.seq}>
            <strong>{TIMELINE_LABEL[entry.type] ?? entry.type.replace(/_/g, " ")}</strong>{" "}
            <span className="svc-admin__muted">
              by {entry.actor} ({entry.actor_role}) at <time dateTime={isoAttr(Date.parse(entry.at))}>{when(entry.at)}</time>
            </span>
            <br />
            <span>{entry.reason}</span>
          </li>
        ))}
      </ol>
    </article>
  );
}

/** Shared form body: required reason, two-factor code, and the error line. */
function ActionForm(props: {
  readonly formId: string;
  readonly reason: string;
  readonly setReason: (v: string) => void;
  readonly code: string;
  readonly setCode: (v: string) => void;
  readonly error: string | null;
  readonly busy: boolean;
  readonly onSubmit: () => void;
  readonly children?: ReactNode;
}): ReactNode {
  const reasonId = useId();
  const codeId = useId();
  const submit = (e: FormEvent): void => {
    e.preventDefault();
    props.onSubmit();
  };
  return (
    <form id={props.formId} className="svc-admin__form svc-lifecycle__form" onSubmit={submit} noValidate>
      {props.children}
      <div className="svc-admin__field">
        <label htmlFor={reasonId}>Reason (required, recorded in the audit trail)</label>
        <textarea id={reasonId} value={props.reason} maxLength={256} rows={3} required onChange={(e) => props.setReason(e.target.value)} />
      </div>
      <div className="svc-admin__field">
        <label htmlFor={codeId}>Two-factor code</label>
        <OtpInput id={codeId} value={props.code} onChange={props.setCode} disabled={props.busy} required align="start" />
      </div>
      {props.error !== null && <p className="svc-admin__err" role="alert">{props.error}</p>}
    </form>
  );
}

function ModalFooter(props: { readonly formId: string; readonly busy: boolean; readonly disabled: boolean; readonly label: string; readonly busyLabel: string; readonly danger?: boolean; readonly onClose: () => void }): ReactNode {
  return (
    <>
      <button type="button" className="svc-admin__btn" onClick={props.onClose} disabled={props.busy}>Cancel</button>
      <button type="submit" form={props.formId} className={props.danger === true ? "svc-admin__btn svc-admin__btn--danger" : "svc-admin__btn"} disabled={props.busy || props.disabled}>
        {props.busy ? props.busyLabel : props.label}
      </button>
    </>
  );
}

/** Runs one lifecycle write and maps a refusal to a plain-language message; `mfa_required` keeps the dialog open. */
function useLifecycleSubmit(onDone: () => Promise<void>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (send: () => Promise<{ ok: true } | { ok: false; code: string }>, clearCode: () => void): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const result = await send();
      if (result.ok) {
        await onDone();
        return;
      }
      // A missing or wrong code re-prompts in place: the reason stays, the code field is cleared.
      if (result.code === "mfa_required") clearCode();
      setError(lifecycleMessage(result.code));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

function SuspendModal(props: { readonly open: boolean; readonly agentRef: string; readonly agentName: string; readonly onClose: () => void; readonly onDone: () => Promise<void> }): ReactNode {
  const formId = useId();
  const [reason, setReason] = useState("");
  const [code, setCode] = useState("");
  const opId = useMemo(() => newOpId(), []);
  const { busy, error, run } = useLifecycleSubmit(props.onDone);
  const submit = (): void => {
    void run(async () => {
      const r = await lifecycleWrite("POST", suspendPath(props.agentRef), { reason: reason.trim(), op_id: opId, mfa_code: code });
      return r.ok ? { ok: true } : { ok: false, code: r.code };
    }, () => setCode(""));
  };
  return (
    <Modal
      open={props.open}
      onClose={busy ? () => undefined : props.onClose}
      role="alertdialog"
      title="Suspend agent"
      description={`Suspend ${props.agentName}? It stops getting tokens and its next boundary decision is denied. You can reinstate it later.`}
      footer={<ModalFooter formId={formId} busy={busy} disabled={reason.trim().length === 0 || code.length !== 6} label="Suspend agent" busyLabel="Suspending…" danger onClose={props.onClose} />}
    >
      <ActionForm formId={formId} reason={reason} setReason={setReason} code={code} setCode={setCode} error={error} busy={busy} onSubmit={submit} />
    </Modal>
  );
}

function ReinstateModal(props: {
  readonly open: boolean;
  readonly agentRef: string;
  readonly agentName: string;
  readonly incidentId: string | null;
  readonly observation: HostObservation | null;
  readonly maxAgeS: number;
  readonly probationSeconds: number;
  readonly onClose: () => void;
  readonly onDone: () => Promise<void>;
}): ReactNode {
  const formId = useId();
  const groupId = useId();
  const [reason, setReason] = useState("");
  const [code, setCode] = useState("");
  const opId = useMemo(() => newOpId(), []);
  const seen = describeObservation(props.observation, props.maxAgeS, Date.now());
  const suggested: HostDecision = seen.kind === "differs" ? "accept_new_host" : seen.kind === "matches" ? "matched" : "accept_unbound";
  const [chosen, setChosen] = useState<HostDecision | null>(null);
  const decision = chosen ?? suggested;
  const { busy, error, run } = useLifecycleSubmit(props.onDone);
  const hours = Math.round((props.probationSeconds / 3600) * 10) / 10;
  const submit = (): void => {
    if (props.incidentId === null) return;
    void run(async () => {
      const r = await lifecycleWrite("POST", reinstatePath(props.agentRef), {
        reason: reason.trim(),
        incident_id: props.incidentId,
        host_decision: decision,
        ...(decision === "accept_new_host" && props.observation?.fingerprint_hash ? { observed_fingerprint_hash: props.observation.fingerprint_hash } : {}),
        op_id: opId,
        mfa_code: code,
      });
      return r.ok ? { ok: true } : { ok: false, code: r.code };
    }, () => setCode(""));
  };
  return (
    <Modal
      open={props.open}
      onClose={busy ? () => undefined : props.onClose}
      title="Reinstate agent"
      description={`Reinstate ${props.agentName}. It is checked against the host it calls in from, then runs with reduced access${props.probationSeconds > 0 ? ` for ${hours} hours (writes are denied, reads work)` : ""}.`}
      footer={<ModalFooter formId={formId} busy={busy} disabled={reason.trim().length === 0 || code.length !== 6 || props.incidentId === null} label="Reinstate agent" busyLabel="Reinstating…" onClose={props.onClose} />}
    >
      <ActionForm formId={formId} reason={reason} setReason={setReason} code={code} setCode={setCode} error={error} busy={busy} onSubmit={submit}>
        <div className="svc-lifecycle__observation" role="group" aria-labelledby={`${groupId}-obs`}>
          <h3 className="svc-admin__section-title" id={`${groupId}-obs`}>Last host observation</h3>
          <p data-observation={seen.kind}>{seen.text}</p>
          {props.observation?.fingerprint_hash ? <p className="svc-admin__muted">Host fingerprint hash: <code>{props.observation.fingerprint_hash}</code></p> : null}
        </div>
        <fieldset className="svc-lifecycle__decisions">
          <legend>Host decision</legend>
          {(Object.keys(DECISION_LABEL) as HostDecision[]).map((value) => (
            <label key={value} className="svc-adm2__radio">
              <input type="radio" name={`${groupId}-decision`} value={value} checked={decision === value} onChange={() => setChosen(value)} />
              {DECISION_LABEL[value]}
            </label>
          ))}
        </fieldset>
      </ActionForm>
    </Modal>
  );
}

/** Admin-only form for the team's probation period and host-observation freshness. */
function ProbationSettingsForm(props: { readonly current: ProbationSettings | undefined; readonly onSaved: () => Promise<void> }): ReactNode {
  const hoursId = useId();
  const ageId = useId();
  const reasonId = useId();
  const codeId = useId();
  const [hours, setHours] = useState<string | null>(null);
  const [age, setAge] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [code, setCode] = useState("");
  const { busy, error, run } = useLifecycleSubmit(props.onSaved);
  const currentHours = props.current === undefined ? "" : String(props.current.probation_seconds / 3600);
  const currentAge = props.current === undefined ? "" : String(Math.round(props.current.host_observation_max_age_s / 60));
  const hoursValue = hours ?? currentHours;
  const ageValue = age ?? currentAge;
  const hoursNumber = Number(hoursValue);
  const ageNumber = Number(ageValue);
  const valid = hoursValue !== "" && ageValue !== "" && Number.isFinite(hoursNumber) && hoursNumber >= 0 && hoursNumber <= 720
    && Number.isInteger(ageNumber) && ageNumber >= 1 && ageNumber <= 60 && reason.trim().length > 0 && code.length === 6;
  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (!valid) return;
    void run(async () => {
      const r = await lifecycleWrite("PUT", PROBATION_SETTINGS_PATH, {
        reason: reason.trim(),
        probation_seconds: Math.round(hoursNumber * 3600),
        host_observation_max_age_s: ageNumber * 60,
        mfa_code: code,
      });
      return r.ok ? { ok: true } : { ok: false, code: r.code };
    }, () => setCode(""));
  };
  return (
    <details className="svc-lifecycle__settings">
      <summary>Probation settings for this team</summary>
      <form className="svc-admin__form svc-lifecycle__form" onSubmit={submit} noValidate aria-label="Probation settings">
        <div className="svc-admin__field">
          <label htmlFor={hoursId}>Probation after reinstatement (hours, 0 turns it off)</label>
          <input id={hoursId} type="number" min={0} max={720} step="any" value={hoursValue} onChange={(e) => setHours(e.target.value)} />
        </div>
        <div className="svc-admin__field">
          <label htmlFor={ageId}>How long a host observation stays valid (minutes, 1 to 60)</label>
          <input id={ageId} type="number" min={1} max={60} step={1} value={ageValue} onChange={(e) => setAge(e.target.value)} />
        </div>
        <div className="svc-admin__field">
          <label htmlFor={reasonId}>Reason (required, recorded in the audit trail)</label>
          <textarea id={reasonId} value={reason} maxLength={256} rows={2} onChange={(e) => setReason(e.target.value)} />
        </div>
        <div className="svc-admin__field">
          <label htmlFor={codeId}>Two-factor code</label>
          <OtpInput id={codeId} value={code} onChange={setCode} disabled={busy} align="start" />
        </div>
        {error !== null && <p className="svc-admin__err" role="alert">{error}</p>}
        <div className="svc-admin__actions">
          <button type="submit" className="svc-admin__btn" disabled={busy || !valid}>{busy ? "Saving…" : "Save settings"}</button>
        </div>
      </form>
    </details>
  );
}

